/**
 * History API Service
 * Handles all API calls related to scan history and performance tracking
 */
import api from '../api';

/**
 * Get scan history with advanced filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.model_type - Filter by model type ('short-term', 'long-term', 'swing-model')
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} params.search_query - Search query for stock symbols
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Page size (default: 20)
 * @returns {Promise} API response with scan history data
 */
export const getScanHistory = async (params = {}) => {
  try {
    const response = await api.get('/api/v1/history/scans', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching scan history:', error);
    throw error;
  }
};

/**
 * Get grouped scan history by date and model
 * @returns {Promise} API response with nested grouped data
 */
export const getGroupedScanHistory = async () => {
  try {
    const response = await api.get('/api/v1/history/grouped-scans');
    return response.data;
  } catch (error) {
    console.error('Error fetching grouped scan history:', error);
    throw error;
  }
};

/**
 * Get detailed information about a specific scan
 * @param {number} scanId - ID of the scan
 * @returns {Promise} API response with scan details
 */
export const getScanDetails = async (scanId) => {
  try {
    const response = await api.get(`/api/v1/history/scans/${scanId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching scan details:', error);
    throw error;
  }
};

/**
 * Trigger performance tracking for all scan results
 * @deprecated Use triggerPerformanceTrackingByDate instead for more granular control
 * This will update the current prices and calculate percentage changes
 * @returns {Promise} API response with tracking status
 */
export const triggerPerformanceTracking = async () => {
  try {
    const response = await api.post('/api/v1/history/track-performance');
    return response.data;
  } catch (error) {
    console.error('Error triggering performance tracking:', error);
    throw error;
  }
};

/**
 * Trigger performance tracking for scan results from a specific date
 * This is the recommended method for updating performance data
 * @param {string} scanDate - Date in YYYY-MM-DD format (e.g., '2025-10-28')
 * @returns {Promise} API response with tracking status
 */
export const triggerPerformanceTrackingByDate = async (scanDate) => {
  try {
    const response = await api.post('/api/v1/history/track-performance/by-date', {
      scan_date: scanDate
    });
    return response.data;
  } catch (error) {
    console.error('Error triggering performance tracking by date:', error);
    throw error;
  }
};

/**
 * Track performance for a single scan result by id
 * @param {number} resultId
 * @returns {Promise} API response
 */
export const trackPerformanceSingle = async (resultId) => {
  try {
    const response = await api.post(`/api/v1/history/track-performance/single/${resultId}`);
    return response.data;
  } catch (error) {
    console.error('Error tracking performance for single result:', error);
    throw error;
  }
};

/**
 * Get performance history for a specific scan result
 * @param {number} resultId - ID of the scan result
 * @returns {Promise} API response with performance history
 */
export const getPerformanceHistory = async (resultId) => {
  try {
    const response = await api.get(`/api/v1/history/performance/${resultId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching performance history:', error);
    throw error;
  }
};

/**
 * Get latest price for a stock symbol (mock implementation - replace with actual API)
 * Note: This endpoint might need to be created in the backend
 * @param {string} symbol - Stock symbol
 * @returns {Promise} Latest price data
 */
export const getLatestPrice = async (symbol) => {
  try {
    // For now, we'll use the performance tracking data
    // You might want to create a dedicated endpoint for real-time prices
    const response = await api.get(`/api/v1/data/latest-price/${symbol}`);
    return response.data;
  } catch (error) {
    // If endpoint doesn't exist, return null
    console.warn(`Latest price not available for ${symbol}`);
    return null;
  }
};

/**
 * Delete a single scan result
 * @param {number} resultId - ID of the scan result to delete
 * @returns {Promise} API response
 */
export const deleteScanResult = async (resultId) => {
  try {
    const response = await api.delete(`/api/v1/history/results/${resultId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting scan result:', error);
    throw error;
  }
};

/**
 * Delete all scans for a specific date
 * @param {string} scanDate - Date in YYYY-MM-DD format (e.g., '2025-10-28')
 * @returns {Promise} API response
 */
export const deleteScansByDate = async (scanDate) => {
  try {
    const response = await api.delete('/api/v1/history/scans-by-date', {
      data: { scan_date: scanDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting scans by date:', error);
    throw error;
  }
};

